/**
 * Branches — physical or organizational sites under a company.
 *
 * Arabic and English (C7): every word from the catalogs; the time zone, an
 * IANA name, is isolated left to right.
 */
import { StatusBadge } from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { ltr } from "@/lib/i18n/format";
import { OrgListPage } from "./OrgListPage";
import { addressLine } from "./types";
import type { BranchRow, CompanyRow } from "./types";

export function BranchesPage() {
  const { t, i18n } = useTranslation();
  const companies = useApiList<CompanyRow>(["org", "companies"], "/companies");

  return (
    <OrgListPage<BranchRow>
      title={t("org.branches.title")}
      subtitle={t("org.branches.subtitle")}
      breadcrumb={[t("org.section"), t("org.branches.title")]}
      path="/branches"
      queryKey="branches"
      createLabel={t("org.branches.new")}
      emptyTitle={t("org.branches.emptyTitle")}
      emptyMessage={t("org.branches.emptyMessage")}
      parent={{
        label: t("fields.company"),
        field: "companyId",
        options: (companies.data?.items ?? []).map((c) => ({
          id: c.id,
          label: `${c.name} (${c.code})`,
        })),
        isLoading: companies.isLoading,
        missingMessage: t("org.branches.missingParent"),
      }}
      extraFields={[
        {
          field: "timezone",
          label: t("fields.timezone"),
          hint: t("org.branches.timezoneHint"),
          placeholder: ltr("Europe/Berlin"),
        },
      ]}
      columns={[
        { key: "name", label: t("fields.name") },
        {
          key: "code",
          label: t("fields.code"),
          render: (r) => <span className="atlas-mono">{r.code}</span>,
        },
        {
          key: "company_id",
          label: t("fields.company"),
          render: (r) =>
            companies.data?.items.find((c) => c.id === r.company_id)?.name ??
            "—",
        },
        {
          key: "timezone",
          label: t("fields.timezone"),
          render: (r) => ltr(r.timezone ?? t("shared.utc")),
        },
        {
          key: "address",
          label: t("fields.address"),
          render: (r) => addressLine(r.address, i18n.language),
        },
        {
          key: "status",
          label: t("fields.status"),
          render: (r) => <StatusBadge status={r.status} type="Branch" />,
        },
      ]}
    />
  );
}
